import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { CreateEncounterPayload } from '@/shared/repositories/encounters/encountersRepository'
import { encountersRepository } from '@/shared/repositories/encounters/encountersRepository'
import type { CreateExamResultPayload } from '@/shared/repositories/exams/examsRepository'
import { examsRepository } from '@/shared/repositories/exams/examsRepository'
import type { CreateEventPayload } from '@/shared/repositories/events/eventsRepository'
import { eventsRepository } from '@/shared/repositories/events/eventsRepository'
import type { UpsertPayload as PatientUpsertPayload } from '@/shared/repositories/patients/patientsRepository'
import { patientsRepository } from '@/shared/repositories/patients/patientsRepository'
import { createId } from '@/shared/lib/id'
import { getErrorMessage, isNavigatorOnline } from '@/shared/lib/network'
import { indexedDBJSON } from '@/shared/lib/persistence'

export type OutboxOperationKind = 'patient.upsert' | 'exam.create' | 'encounter.create' | 'event.create'

export const OUTBOX_KIND_LABEL: Record<OutboxOperationKind, string> = {
  'patient.upsert': 'Paciente',
  'exam.create': 'Exame',
  'encounter.create': 'Atendimento',
  'event.create': 'Evento',
}

interface OutboxPayloadByKind {
  'patient.upsert': PatientUpsertPayload
  'exam.create': CreateExamResultPayload
  'encounter.create': CreateEncounterPayload
  'event.create': CreateEventPayload
}

type OutboxItemStatus = 'pending' | 'running' | 'failed'

interface OutboxItemBase {
  id: string
  status: OutboxItemStatus
  retryCount: number
  createdAt: string
  updatedAt: string
  lastAttemptAt?: string
  lastError?: string
}

export type OutboxItem = {
  [K in OutboxOperationKind]: OutboxItemBase & {
    kind: K
    payload: OutboxPayloadByKind[K]
  }
}[OutboxOperationKind]

interface OutboxStoreState {
  items: OutboxItem[]
  isRetrying: boolean
  lastSuccessAt?: string
  enqueue: <K extends OutboxOperationKind>(
    kind: K,
    payload: OutboxPayloadByKind[K],
    initialError?: string,
  ) => string
  retryItem: (itemId: string) => Promise<boolean>
  retryAll: () => Promise<void>
  clearFailed: () => void
  purgeStaleFailedItems: (maxAgeMs?: number) => number
  remove: (itemId: string) => void
  setupAutoRetry: () => void
  teardownAutoRetry: () => void
}

const RETRY_INTERVAL_MS = 45_000
export const FAILED_OUTBOX_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000

const isFailedItemStale = (item: OutboxItem, cutoffMs: number): boolean => {
  if (item.status !== 'failed') return false
  const updatedAt = new Date(item.updatedAt).getTime()
  return Number.isFinite(updatedAt) && updatedAt < cutoffMs
}

let autoRetryInitialized = false
/** `window.setInterval` → `number` no DOM; evita colisão com `ReturnType<typeof setInterval>` (Node `Timeout`). */
let autoRetryIntervalId: number | null = null
let autoRetryOnlineHandler: (() => void) | null = null

const executeOutboxItem = async (item: OutboxItem): Promise<void> => {
  switch (item.kind) {
    case 'patient.upsert':
      await patientsRepository.upsert(item.payload)
      return
    case 'exam.create':
      await examsRepository.createResult(item.payload)
      return
    case 'encounter.create':
      await encountersRepository.create(item.payload)
      return
    case 'event.create':
      await eventsRepository.create(item.payload)
      return
    default:
      return
  }
}

export const useOutboxStore = create<OutboxStoreState>()(
  persist(
    (set, get) => ({
      items: [],
      isRetrying: false,
      lastSuccessAt: undefined,

      enqueue: (kind, payload, initialError) => {
        const now = new Date().toISOString()
        const item = {
          id: createId(),
          kind,
          payload,
          status: 'pending',
          retryCount: 0,
          createdAt: now,
          updatedAt: now,
          lastError: initialError,
        } as OutboxItem

        set((state) => ({
          items: [...state.items, item],
        }))

        if (isNavigatorOnline()) {
          void get().retryAll()
        }

        return item.id
      },

      retryItem: async (itemId) => {
        const current = get().items.find((item) => item.id === itemId)
        if (!current) return true

        const attemptedAt = new Date().toISOString()
        set((state) => ({
          items: state.items.map((item) =>
            item.id === itemId
              ? {
                  ...item,
                  status: 'running',
                  updatedAt: attemptedAt,
                  lastAttemptAt: attemptedAt,
                }
              : item,
          ),
        }))

        try {
          await executeOutboxItem(current)
          const successAt = new Date().toISOString()
          set((state) => ({
            items: state.items.filter((item) => item.id !== itemId),
            lastSuccessAt: successAt,
          }))
          return true
        } catch (error) {
          const failedAt = new Date().toISOString()
          const message = getErrorMessage(
            error,
            'Não foi possível reenviar a alteração pendente.',
          )
          set((state) => ({
            items: state.items.map((item) =>
              item.id === itemId
                ? {
                    ...item,
                    status: 'failed',
                    retryCount: item.retryCount + 1,
                    lastError: message,
                    updatedAt: failedAt,
                    lastAttemptAt: failedAt,
                  }
                : item,
            ),
          }))
          return false
        }
      },

      retryAll: async () => {
        if (get().isRetrying || !isNavigatorOnline()) return
        set({ isRetrying: true })

        try {
          const retryable = get().items
            .filter((item) => item.status === 'pending' || item.status === 'failed')
            .map((item) => item.id)

          for (const itemId of retryable) {
            await get().retryItem(itemId)
          }
        } finally {
          set({ isRetrying: false })
        }
      },

      clearFailed: () => {
        set((state) => ({
          items: state.items.filter((item) => item.status !== 'failed'),
        }))
      },

      purgeStaleFailedItems: (maxAgeMs = FAILED_OUTBOX_MAX_AGE_MS) => {
        const cutoffMs = Date.now() - maxAgeMs
        let purged = 0
        set((state) => {
          const nextItems = state.items.filter((item) => {
            if (!isFailedItemStale(item, cutoffMs)) return true
            purged += 1
            return false
          })
          return { items: nextItems }
        })
        return purged
      },

      remove: (itemId) => {
        set((state) => ({
          items: state.items.filter((item) => item.id !== itemId),
        }))
      },

      setupAutoRetry: () => {
        if (typeof window === 'undefined' || autoRetryInitialized) return
        autoRetryInitialized = true

        get().purgeStaleFailedItems()

        const triggerRetry = () => {
          void get().retryAll()
        }
        autoRetryOnlineHandler = triggerRetry

        window.addEventListener('online', triggerRetry)
        autoRetryIntervalId = window.setInterval(() => {
          if (!isNavigatorOnline()) return
          void get().retryAll()
        }, RETRY_INTERVAL_MS)
      },

      teardownAutoRetry: () => {
        if (typeof window === 'undefined') return
        if (autoRetryIntervalId !== null) {
          clearInterval(autoRetryIntervalId)
          autoRetryIntervalId = null
        }
        if (autoRetryOnlineHandler !== null) {
          window.removeEventListener('online', autoRetryOnlineHandler)
          autoRetryOnlineHandler = null
        }
        autoRetryInitialized = false
      },
    }),
    {
      name: 'synap-offline-outbox',
      storage: indexedDBJSON,
      partialize: (state) => ({
        items: state.items,
        lastSuccessAt: state.lastSuccessAt,
      }),
    },
  ),
)
